import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';

interface PageActionsContextValue {
  setPageActions: (node: ReactNode | null) => void;
}

const PageActionsContext = createContext<PageActionsContextValue>({
  setPageActions: () => { /* no-op when provider isn't mounted */ },
});

export function PageActionsProvider({
  onChange,
  children,
}: {
  onChange: (node: ReactNode | null) => void;
  children: ReactNode;
}) {
  const setPageActions = useCallback((node: ReactNode | null) => onChange(node), [onChange]);
  return (
    <PageActionsContext.Provider value={{ setPageActions }}>
      {children}
    </PageActionsContext.Provider>
  );
}

/** Register a node to render in the parent layout's sidebar action slot for
 * as long as the calling component is mounted. Pass `null` to clear early. */
export function usePageActions(node: ReactNode | null): void {
  const { setPageActions } = useContext(PageActionsContext);
  useEffect(() => {
    setPageActions(node);
    return () => setPageActions(null);
  }, [node, setPageActions]);
}

/** Convenience for parent layouts: wires a useState pair into a
 * PageActionsProvider in one call. Returns the node so the layout can
 * render it wherever it wants in its own JSX. */
export function usePageActionsState(): [ReactNode | null, (n: ReactNode | null) => void] {
  const [node, setNode] = useState<ReactNode | null>(null);
  return [node, setNode];
}

/** App shell for pages that need a sticky header (action bar) and/or
 * sticky footer (status strip). Renders absolute-positioned inside a
 * `position: relative` parent (typically a layout's <main>), so it OWNS
 * the full vertical space and its internal sections behave correctly:
 *   - Header  shrink-0  (stays at top, doesn't scroll)
 *   - Middle  flex-1 overflow-y-auto  (the ONE scroll region)
 *   - Footer  shrink-0  (stays at bottom, doesn't scroll)
 *
 * Use:
 *   <PageShell header={<MyHeader/>} footer={<MyFooter/>}>
 *     <MyTabsAndContent />
 *   </PageShell>
 */
export function PageShell({
  header,
  footer,
  children,
}: {
  header?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="absolute inset-6 flex flex-col">
      {header && <div className="shrink-0 pb-3">{header}</div>}
      <div className="flex-1 min-h-0 overflow-y-auto">{children}</div>
      {footer && <div className="shrink-0 pt-3">{footer}</div>}
    </div>
  );
}
